import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText } from 'lucide-react';

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmt2 = (n) => Number(n ?? 0).toFixed(2);

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const buildGstSummary = (items) => {
  const map = {};
  for (const item of items) {
    const rate = item.gst_rate ?? 0;
    if (!map[rate]) map[rate] = { taxable: 0, gst: 0 };
    const gst     = item.gst_amount ?? 0;
    const taxable = (item.item_total ?? 0) - gst;
    map[rate].taxable += taxable;
    map[rate].gst     += gst;
  }
  // Include 0% rows too so the table shows all rates present in the bill
  return Object.entries(map)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([rate, val]) => ({
      rate:    Number(rate),
      taxable: Math.round(val.taxable * 100) / 100,
      gst:     Math.round(val.gst * 100) / 100,
      cgst:    Math.round(val.gst / 2 * 100) / 100,
      sgst:    Math.round(val.gst / 2 * 100) / 100,
    }));
};

// Simple Indian-system number to words (handles up to crores)
const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
              'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
              'Seventeen', 'Eighteen', 'Nineteen'];
const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function numToWords(n) {
  n = Math.round(n);
  if (n === 0) return 'Zero';
  if (n < 0) return 'Minus ' + numToWords(-n);

  let result = '';
  if (n >= 10000000) { result += numToWords(Math.floor(n / 10000000)) + ' Crore '; n %= 10000000; }
  if (n >= 100000)   { result += numToWords(Math.floor(n / 100000))   + ' Lakh ';  n %= 100000; }
  if (n >= 1000)     { result += numToWords(Math.floor(n / 1000))     + ' Thousand '; n %= 1000; }
  if (n >= 100)      { result += ones[Math.floor(n / 100)]            + ' Hundred '; n %= 100; }
  if (n >= 20)       { result += tens[Math.floor(n / 10)] + ' '; n %= 10; }
  if (n > 0)         { result += ones[n] + ' '; }
  return result.trim();
}

function amountInWords(total) {
  const rupees = Math.floor(total);
  const paise  = Math.round((total - rupees) * 100);
  let w = numToWords(rupees) + ' Rupees';
  if (paise > 0) w += ' And ' + numToWords(paise) + ' Paise';
  w += ' Only';
  return w;
}

const paymentLabel = (sale) => {
  if (sale.payment_mode === 'split' && sale.split_payments?.length) {
    return sale.split_payments
      .filter(p => p.amount > 0)
      .map(p => `${p.mode.toUpperCase()} ₹${fmt2(p.amount)}`)
      .join(' + ');
  }
  return (sale.payment_mode || 'Cash').replace(/^\w/, c => c.toUpperCase());
};

// ── Invoice Content ────────────────────────────────────────────────────────────

export function WholesaleInvoiceContent({ sale, storeProfile }) {
  const items          = sale.items ?? [];
  const gstSummary     = buildGstSummary(items);
  const totalGst       = items.reduce((s, i) => s + (i.gst_amount ?? 0), 0);
  const subtotal       = sale.subtotal ?? items.reduce((s, i) => s + (i.item_total ?? 0), 0);
  const overallDisc    = sale.overall_discount ?? 0;
  const roundOff       = sale.round_off ?? 0;
  const grandTotal     = sale.total_amount ?? 0;
  const totalTaxable   = subtotal - totalGst - overallDisc;

  const borderCell = { border: '1px solid #333', padding: '4px 6px' };
  const th         = { ...borderCell, background: '#f0f0f0', fontWeight: '600', fontSize: '11px', textAlign: 'center' };
  const td         = { ...borderCell, fontSize: '11px' };
  const tdR        = { ...td, textAlign: 'right' };
  const tdC        = { ...td, textAlign: 'center' };

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', color: '#111', width: '100%', boxSizing: 'border-box' }}>

      {/* ── Store header ── */}
      <div style={{ textAlign: 'center', borderBottom: '2px solid #333', paddingBottom: '8px', marginBottom: '8px' }}>
        <div style={{ fontSize: '20px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>
          {storeProfile?.shop_name || 'Store'}
        </div>
        {storeProfile?.address && (
          <div style={{ fontSize: '11px', marginTop: '2px' }}>{storeProfile.address}</div>
        )}
        {storeProfile?.phone && (
          <div style={{ fontSize: '11px' }}>Mob: {storeProfile.phone}</div>
        )}
        {storeProfile?.gstin && (
          <div style={{ fontSize: '11px', fontWeight: '600' }}>GSTIN: {storeProfile.gstin}</div>
        )}
        {storeProfile?.food_license_number && (
          <div style={{ fontSize: '11px', fontWeight: '600' }}>FSSAI: {storeProfile.food_license_number}</div>
        )}
      </div>

      {/* ── TAX INVOICE title ── */}
      <div style={{ textAlign: 'center', fontSize: '15px', fontWeight: '700', letterSpacing: '2px', marginBottom: '8px', textDecoration: 'underline' }}>
        TAX INVOICE
      </div>

      {/* ── Meta row: Bill To | Invoice details ── */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
        <tbody>
          <tr>
            {/* Bill To */}
            <td style={{ width: '55%', border: '1px solid #333', padding: '6px 8px', verticalAlign: 'top' }}>
              <div style={{ fontSize: '10px', fontWeight: '600', marginBottom: '2px' }}>BILL TO:</div>
              <div style={{ fontSize: '12px', fontWeight: '700' }}>
                {sale.customer_name || 'Walk-in Customer'}
              </div>
              {sale.customer_address && (
                <div style={{ fontSize: '11px', marginTop: '2px' }}>{sale.customer_address}</div>
              )}
              {sale.customer_mobile && (
                <div style={{ fontSize: '11px', marginTop: '2px' }}>Mob: {sale.customer_mobile}</div>
              )}
              {sale.customer_gst_number && (
                <div style={{ fontSize: '11px', marginTop: '2px', fontWeight: '600' }}>GSTIN: {sale.customer_gst_number}</div>
              )}
              {sale.customer_fssai_number && (
                <div style={{ fontSize: '11px', marginTop: '2px', fontWeight: '600' }}>FSSAI: {sale.customer_fssai_number}</div>
              )}
              {sale.customer_name && (() => {
                const khataCleared = sale.khata_cleared ?? 0;
                const khataAdded   = sale.khata_added ?? (
                  sale.payment_mode === 'khata'
                    ? sale.total_amount
                    : (sale.split_payments?.reduce((s, p) => p.mode === 'khata' ? s + p.amount : s, 0) ?? 0)
                );
                if (khataAdded === 0 && khataCleared === 0) return null;
                const existingKhata = sale.existing_khata_balance
                  ?? (khataCleared > 0 ? (sale.khata_remaining ?? 0) + khataCleared : 0);
                const netChange  = khataAdded - khataCleared;
                const newBalance = existingKhata + netChange;
                const changeLabel   = khataAdded > 0 ? 'Khata Added' : khataCleared > 0 ? 'Khata Cleared' : 'Khata Added/Cleared';
                const changeDisplay = netChange > 0 ? `+₹${fmt2(khataAdded)}` : netChange < 0 ? `-₹${fmt2(khataCleared)}` : '₹0';
                const row = { display: 'flex', justifyContent: 'space-between', fontSize: '10px', paddingBottom: '2px' };
                return (
                  <div style={{ marginTop: '6px', borderTop: '1px dashed #999', paddingTop: '5px' }}>
                    <div style={row}>
                      <span>{sale.customer_name} Existing Khata</span>
                      <span>₹{fmt2(existingKhata)}</span>
                    </div>
                    <div style={row}>
                      <span>{sale.customer_name} {changeLabel}</span>
                      <span style={{ fontWeight: '600' }}>{changeDisplay}</span>
                    </div>
                    <div style={{ ...row, color: '#555' }}>
                      <span>Outstanding Balance</span>
                      <span>₹{fmt2(newBalance)}</span>
                    </div>
                    <div style={{ ...row, fontWeight: '700', borderTop: '1px solid #999', paddingTop: '3px', marginTop: '2px' }}>
                      <span>TOTAL COLLECTION</span>
                      <span>₹{fmt2(sale.total_amount + khataCleared)}</span>
                    </div>
                  </div>
                );
              })()}
            </td>
            {/* Invoice details */}
            <td style={{ width: '45%', border: '1px solid #333', padding: '6px 8px', verticalAlign: 'top' }}>
              <table style={{ width: '100%', fontSize: '11px' }}>
                <tbody>
                  <tr>
                    <td style={{ paddingBottom: '2px', fontWeight: '600' }}>Invoice No</td>
                    <td style={{ paddingBottom: '2px', textAlign: 'right', fontWeight: '700' }}>
                      {sale.bill_number && sale.bill_number !== 'PENDING' ? sale.bill_number : '—'}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ paddingBottom: '2px', fontWeight: '600' }}>Date</td>
                    <td style={{ paddingBottom: '2px', textAlign: 'right' }}>{fmtDate(sale.created_at)}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: '600' }}>Payment</td>
                    <td style={{ textAlign: 'right' }}>{paymentLabel(sale)}</td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Items table ── */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
        <thead>
          <tr>
            <th style={{ ...th, width: '28px' }}>Sr.</th>
            <th style={{ ...th, textAlign: 'left' }}>Name of Product</th>
            <th style={{ ...th, width: '52px' }}>HSN</th>
            <th style={{ ...th, width: '36px' }}>Qty</th>
            <th style={{ ...th, width: '64px' }}>Rate</th>
            <th style={{ ...th, width: '72px' }}>Taxable Amt</th>
            <th style={{ ...th, width: '40px' }}>Dis%</th>
            <th style={{ ...th, width: '40px' }}>GST%</th>
            <th style={{ ...th, width: '72px' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const mrp      = item.mrp ?? item.unit_price;
            const discPct  = item.discount ?? (mrp > item.unit_price ? Math.round((1 - item.unit_price / mrp) * 100) : 0);
            const gstAmt   = item.gst_amount ?? 0;
            const taxable  = (item.item_total ?? 0) - gstAmt;
            return (
              <tr key={i}>
                <td style={{ ...tdC }}>{i + 1}</td>
                <td style={{ ...td }}>{item.product_name}</td>
                <td style={{ ...tdC }}>{item.hsn_code || '—'}</td>
                <td style={{ ...tdC }}>{item.quantity}</td>
                <td style={{ ...tdR }}>₹{fmt2(item.unit_price)}</td>
                <td style={{ ...tdR }}>₹{fmt2(taxable)}</td>
                <td style={{ ...tdC }}>{discPct > 0 ? discPct + '%' : '—'}</td>
                <td style={{ ...tdC }}>{(item.gst_rate ?? 0) > 0 ? item.gst_rate + '%' : '0%'}</td>
                <td style={{ ...tdR, fontWeight: '600' }}>₹{fmt2(item.item_total)}</td>
              </tr>
            );
          })}
          {/* Filler rows to maintain table height for small bills */}
          {items.length < 5 && Array.from({ length: 5 - items.length }).map((_, i) => (
            <tr key={`fill-${i}`}>
              {Array.from({ length: 9 }).map((_, j) => (
                <td key={j} style={{ ...td, height: '20px' }}>&nbsp;</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── Bottom section: GST summary + Totals side by side ── */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
        <tbody>
          <tr style={{ verticalAlign: 'top' }}>
            {/* GST summary */}
            <td style={{ width: '55%', border: '1px solid #333', padding: '6px 8px' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', marginBottom: '4px' }}>GST SUMMARY</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: '36px' }}>GST %</th>
                    <th style={{ ...th }}>Taxable Amt</th>
                    <th style={{ ...th }}>SGST</th>
                    <th style={{ ...th }}>CGST</th>
                    <th style={{ ...th }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {gstSummary.map(g => (
                    <tr key={g.rate}>
                      <td style={{ ...tdC, fontSize: '10px' }}>{g.rate}%</td>
                      <td style={{ ...tdR, fontSize: '10px' }}>{fmt2(g.taxable)}</td>
                      <td style={{ ...tdR, fontSize: '10px' }}>{fmt2(g.sgst)}</td>
                      <td style={{ ...tdR, fontSize: '10px' }}>{fmt2(g.cgst)}</td>
                      <td style={{ ...tdR, fontSize: '10px' }}>{fmt2(g.gst)}</td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr style={{ fontWeight: '700', background: '#f0f0f0' }}>
                    <td style={{ ...tdC, fontSize: '10px' }}>Total</td>
                    <td style={{ ...tdR, fontSize: '10px' }}>{fmt2(totalTaxable)}</td>
                    <td style={{ ...tdR, fontSize: '10px' }}>{fmt2(totalGst / 2)}</td>
                    <td style={{ ...tdR, fontSize: '10px' }}>{fmt2(totalGst / 2)}</td>
                    <td style={{ ...tdR, fontSize: '10px' }}>{fmt2(totalGst)}</td>
                  </tr>
                </tbody>
              </table>
            </td>

            {/* Totals block */}
            <td style={{ width: '45%', border: '1px solid #333', padding: '6px 8px' }}>
              <table style={{ width: '100%', fontSize: '11px' }}>
                <tbody>
                  <tr>
                    <td style={{ paddingBottom: '3px' }}>Subtotal</td>
                    <td style={{ textAlign: 'right', paddingBottom: '3px' }}>₹{fmt2(subtotal)}</td>
                  </tr>
                  {overallDisc > 0 && (
                    <tr>
                      <td style={{ paddingBottom: '3px' }}>Discount</td>
                      <td style={{ textAlign: 'right', paddingBottom: '3px', color: '#555' }}>- ₹{fmt2(overallDisc)}</td>
                    </tr>
                  )}
                  <tr>
                    <td style={{ paddingBottom: '3px' }}>Total GST</td>
                    <td style={{ textAlign: 'right', paddingBottom: '3px' }}>₹{fmt2(totalGst)}</td>
                  </tr>
                  {roundOff !== 0 && (
                    <tr>
                      <td style={{ paddingBottom: '3px', color: '#777' }}>Round Off</td>
                      <td style={{ textAlign: 'right', paddingBottom: '3px', color: '#777' }}>
                        {roundOff > 0 ? '+' : ''}₹{fmt2(roundOff)}
                      </td>
                    </tr>
                  )}
                  <tr style={{ borderTop: '2px solid #333' }}>
                    <td style={{ paddingTop: '4px', fontWeight: '700', fontSize: '13px' }}>Invoice Amt</td>
                    <td style={{ textAlign: 'right', paddingTop: '4px', fontWeight: '700', fontSize: '13px' }}>
                      ₹{fmt2(grandTotal)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Amount in words ── */}
      <div style={{ border: '1px solid #333', padding: '5px 8px', marginBottom: '8px', fontSize: '11px' }}>
        <span style={{ fontWeight: '600' }}>Total Inwords: </span>
        <span>{amountInWords(grandTotal)}</span>
      </div>

      {/* ── Footer: declaration + signature ── */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr style={{ verticalAlign: 'top' }}>
            <td style={{ width: '60%', border: '1px solid #333', padding: '6px 8px', fontSize: '10px', color: '#444' }}>
              <div>Certified that the particulars given above are true and correct.</div>
              {storeProfile?.return_policy && (
                <div style={{ marginTop: '4px' }}>{storeProfile.return_policy}</div>
              )}
              <div style={{ marginTop: '4px' }}>E &amp; O.E.</div>
            </td>
            <td style={{ width: '40%', border: '1px solid #333', padding: '6px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '28px' }}>
                FOR {(storeProfile?.shop_name || 'STORE').toUpperCase()}
              </div>
              <div style={{ fontSize: '10px', borderTop: '1px solid #999', paddingTop: '4px' }}>
                Authorised Signatory
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Powered by ── */}
      <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '9px', color: '#aaa' }}>
        Powered by <span style={{ color: '#059669', fontWeight: '700' }}>KiranaFlow</span> · kiranaflow.com
      </div>
    </div>
  );
}

// ── Modal wrapper ──────────────────────────────────────────────────────────────

export default function WholesaleInvoiceModal({ sale, storeProfile, onClose }) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'p' || e.key === 'P') window.print();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      {/* A4 print styles — uses a different wrapper class so thermal receipt CSS is untouched */}
      <style>{`
        .invoice-print-wrapper { display: none; }
        @media print {
          body > *:not(.invoice-print-wrapper) { display: none !important; }
          .invoice-print-wrapper { display: block !important; width: 100%; box-sizing: border-box; background: white; }
          @page { size: A4 portrait; margin: 12mm 14mm; }
        }
      `}</style>

      {/* Print target in <body> */}
      {createPortal(
        <div className="invoice-print-wrapper">
          <WholesaleInvoiceContent sale={sale} storeProfile={storeProfile} />
        </div>,
        document.body
      )}

      {/* Modal overlay */}
      <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
            <h2 className="font-bold text-base flex items-center gap-2">
              <FileText className="h-4 w-4" /> Wholesale Invoice
            </h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable preview */}
          <div className="overflow-y-auto flex-1 px-6 py-5 bg-gray-100">
            <div className="bg-white shadow-lg rounded p-6 mx-auto" style={{ maxWidth: '640px' }}>
              <WholesaleInvoiceContent sale={sale} storeProfile={storeProfile} />
            </div>
          </div>

          {/* Actions */}
          <div className="px-5 py-3 border-t border-border flex gap-3 shrink-0">
            <button
              onClick={() => window.print()}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
            >
              <FileText className="h-4 w-4" />
              Print Invoice (A4)
            </button>
            <button
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted/60"
            >
              Close
            </button>
          </div>

          <p className="text-center text-xs text-muted-foreground pb-2">
            P print · Esc close
          </p>
        </div>
      </div>
    </>
  );
}
